import WisdomLogo from "../../medias/images/Wisdom logo transparent.png";
import "./SignIn.css";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Keycloak from "keycloak-js";
import LoadingSpinner from "../../components/loadSpinner/LoadingSpinner.jsx";
import { Toast } from "primereact/toast";
import { requestAuthLogin } from "../../services/wisdomServices/authService";
import { requestGetSettings } from "../../services/wisdomServices/settingsService";
import { getSessionItem, removeSessionItem, setSessionItem } from "../../utils/helper/session";
import { Button } from "primereact/button";

/**
 * @function SignIn
 * @returns {JSX.Element} - sign-in screen component
 */
function SignIn() {
  const currentYear = new Date().getFullYear()
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const toast = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loadSSO, setLoadSSO] = useState(null);
  const [isLoading, setIsLoading] = useState(true); 
  const { hash } = useLocation();

  /**
   * @function showError
   * @param {string} text
   * @description - displays an error toast message 
   */
  const showError = (text) => {
    toast.current.show({ severity: 'error', summary: 'Error', detail: text, life: 3000 });
  };
  /**
   * @async
   * @function login
   * @param {boolean} sso 
   * @param {string} ssoToken 
   * @param {boolean} isSso 
   * @description - performs login using either SSO or email/password
   */
  const login = async (sso, ssoToken, isSso) => {
    setIsLoading(true); 
    let keys = {};
    if (isSso) {
      keys = { "sso": sso, "ssoToken": ssoToken };
    } else {
      keys = { "email": email, "password": password, "sso": false };
    }

    try {
      removeSessionItem("User");
      await requestAuthLogin(keys);
      setSessionItem("TrackerStatus", false);
      setSessionItem("timeSpentInSeconds", 0);
      let user = getSessionItem("User");
      if (user) {
        window.location.href = "/summary";
      } else {
        showError('Wrong username or password');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false); 
    }
  };

  /**
   * @async
   * @function sso
   * @description - starts the SSO authentication flow
   */
  const sso = async () => {
    setIsLoading(true); 
    await initKeyCloak();
  };

  /**
   * @async
   * @function initKeyCloak
   * @description - initializes Keycloak authentication
   */
  const initKeyCloak = async () => {
    let settings;
    let settingsResponse = await requestGetSettings()
    if(settingsResponse){
      settings = JSON.parse(settingsResponse.value)
    }
    let keycloak = new Keycloak({
      url: settings.keycloakURL,
      realm: settings.realm,
      clientId: settings.clientID,
    });
    
    await keycloak.init({
      onLoad: settings.byPass ? 'check-sso' : 'login-required',
      redirectUri: settings.ssoRedirectUrl,
      enableLogging: true,
      useNonce: false,
    }).then(async auth => {
      if (auth && keycloak.idTokenParsed !== null) {
        setEmail(keycloak.idTokenParsed.email);
        await login(true, keycloak.token, true);
      } else if (settings.byPass) {
        keycloak.login();
      }
    }).finally(() => setIsLoading(false)); 
  };

  useEffect(() => {
    const init = async () => {
      if (hash !== "") {
        await initKeyCloak();
      }

      if (loadSSO == null) {
        let settings;
        const settingsResponse = await requestGetSettings();
        if (settingsResponse) {
          settings = JSON.parse(settingsResponse.value);
          setLoadSSO(settings.showSSO);
        }
      }

      setIsLoading(false);
    };

    init();
  }, []);


  useEffect(() => {
    if (password !== '' || email !== '') {
      const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
          login();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [login]);

  return (
    <div className="login-page-wrapper">
      <Toast ref={toast} position="top-center" />
      {isLoading && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )} 
      {loadSSO !== null && (
        <div className="login-main-container">
          <div className="floating-login-container">
            <img className="wisdom-logo-img" src={WisdomLogo} alt="Wisdom Logo" />
            <hr className="divider top-divider" />
            <div className="signed-header">Log in using your account on</div>
            <div className="sign-in-page-section">
              <div className="signIn-btns">
                <Button 
                  className="user-login-btn"
                  label="Login with sirius email" 
                  icon="pi pi-envelope" 
                  severity="info" 
                  onClick={sso} 
                  disabled={isLoading}
                />
                <hr className="divider" />
                <div className="first-time-here-tip">
                  <p className="signed-header">Is your first time here?</p>
                  <p>Use the button above to login and provide your email credentials.</p>
                </div>
                <hr className="divider" />
                <Button 
                  className="user-login-btn"
                  label="Admin Login"
                  icon="pi pi-user-plus"
                  severity="info"
                  onClick={() => setShowAdminLogin(!showAdminLogin)} 
                  disabled={isLoading}
                />
                {showAdminLogin && (
                  <>
                    <input className="login-page-input-text" type="email" placeholder="Email" onChange={(e) => setEmail(e.target.value)} disabled={isLoading} />
                    <input className="login-page-input-text" type="password" placeholder="Password" onChange={(e) => setPassword(e.target.value)} disabled={isLoading} />
                    <Button 
                      className="user-login-btn"
                      label="Login"
                      icon="pi pi-sign-in"
                      severity="success"
                      disabled={isLoading || email === "" || password === "" }
                      onClick={login}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <footer className="login-page-footer">
        <div>
          <a href="https://sirius-support.com/terms-of-service/" target="_blank">Terms of Service</a> |{" "}
          <a href="https://sirius-support.com/privacy-policy/" target="_blank">Privacy Policy</a>
        </div>
        <div>
          <p>&copy; {currentYear} <a href="https://sirius-support.com" target="_blank">Sirius Support</a></p>
        </div>
      </footer>
    </div>
  );
}

export default SignIn;
